import { useEffect, useState } from 'react';
import axios from 'axios';
import useModalStore from '../../../zustand/store/useModalStore';
import useConfirm from '../../../hooks/useConfirm';
import { useModalOpen } from '../../../hooks/useToastModalOpen';
import { useDynamicStyle } from '../../../hooks/useDynamicStyle';
import useMenuPermission from '../../../hooks/useMenuPermission';
import { ROLE_API, handleRoleApiError, validateRoleForm } from './roleMngApi';

import '../../../css/role-mng.css';

/** 'YYYYMMDDHH24MISS' → 'YYYY-MM-DD HH:MI' */
const formatDt = (value) => {
    if (!value || value.length < 8) return value ?? '';
    const date = `${value.substring(0, 4)}-${value.substring(4, 6)}-${value.substring(6, 8)}`;
    return value.length >= 12 ? `${date} ${value.substring(8, 10)}:${value.substring(10, 12)}` : date;
};

const formInit = { rolename: '', useyn: 'Y', defaultyn: 'N', bigo: '' };

/**
 * 롤 상세정보 · 수정 · 삭제 (롤권한관리 하단)
 * @param detail selectRoleDetail.do 응답 roleDetail (미선택 시 null)
 * @param onSaved 수정 성공 콜백 (roleno 전달)
 * @param onDeleted 삭제 성공 콜백
 */
const RoleDetail = ({ detail, onSaved, onDeleted }) => {

    const notSelected = !detail;
    const [form, setForm] = useState(formInit);
    const [saving, setSaving] = useState(false);

    const showNotiModal = useModalStore(state => state.showNotiModal);
    const showPromptModal = useModalStore(state => state.showPromptModal);
    const confirm = useConfirm();
    const { toastModalOpen } = useModalOpen();
    const { primaryBackColor } = useDynamicStyle();
    // 롤권한관리 메뉴 권한(롤 권한 + 개인 권한). 버튼은 그대로 두고 권한이 없으면 누를 때 안내한다(서버도 같은 기준으로 막는다)
    const { withPermission } = useMenuPermission();

    useEffect(() => {
        if (detail) {
            setForm({
                rolename: detail.rolename ?? '',
                useyn: detail.useyn ?? 'Y',
                defaultyn: detail.defaultyn ?? 'N',
                bigo: detail.bigo ?? '',
            });
        } else {
            setForm(formInit);
        }
    }, [detail]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value
        }));
    };

    const update = async () => {
        if (notSelected) {
            showNotiModal('롤을 선택해주세요.');
            return;
        }
        const isSame = form.rolename.trim() === (detail.rolename ?? '')
            && form.useyn === (detail.useyn ?? 'Y')
            && form.defaultyn === (detail.defaultyn ?? 'N')
            && form.bigo.trim() === (detail.bigo ?? '');
        if (isSame) {
            showNotiModal('변경사항이 없습니다.');
            return;
        }
        const error = validateRoleForm({ authjobs: detail.authjobs, roleno: detail.roleno, rolename: form.rolename, bigo: form.bigo });
        if (error) {
            showNotiModal(error);
            return;
        }
        const ok = await confirm(`롤 [${detail.rolename}] 정보를 수정하시겠습니까?`);
        if (!ok) return;

        try {
            setSaving(true);
            const response = await axios.post(ROLE_API.update, {
                roleno: detail.roleno,
                rolename: form.rolename.trim(),
                useyn: form.useyn,
                defaultyn: form.defaultyn,
                bigo: form.bigo.trim(),
                ord: detail.ord ?? 0,
            });
            const { result, resultMessage } = response.data.resultMap;
            if (result) {
                toastModalOpen(resultMessage);
                onSaved(detail.roleno);
            } else {
                showNotiModal(resultMessage);
            }
        } catch (error) {
            handleRoleApiError(error, showNotiModal, '롤 정보 수정 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    // 롤 삭제: 서버 사전 점검(차단 사유·영향 범위) → 영향 안내 확인 → 삭제 사유 입력(필수) → 삭제
    const remove = async () => {
        if (notSelected) {
            showNotiModal('롤을 선택해주세요.');
            return;
        }
        try {
            setSaving(true);
            const checkResponse = await axios.get(ROLE_API.deleteCheck, { params: { roleno: detail.roleno } });
            const check = checkResponse.data.deleteCheck ?? {};
            if (!check.deletable) {
                showNotiModal(`롤 [${detail.rolename}](${detail.roleno})은(는) 삭제할 수 없습니다.\n\n${(check.messages ?? []).join('\n')}`);
                return;
            }
            const ok = await confirm(
                `롤 [${detail.rolename}](${detail.roleno})을(를) 삭제하시겠습니까?\n\n`
                + `함께 삭제되는 메뉴 접근권한: ${check.authCnt ?? 0}건\n`
                + `이 롤을 참조하는 탈퇴 사용자: ${check.withdrawnCnt ?? 0}명\n\n`
                + `삭제 내역은 삭제 로그에 보관되어 DBA가 복구할 수 있습니다.`,
                { okText: '삭제', cancelText: '취소' }
            );
            if (!ok) return;

            const prompt = await showPromptModal('삭제 사유를 입력해주세요. (필수, 삭제 로그에 기록)', { placeholder: '예) 미사용 롤 정리' });
            if (!prompt?.result || !prompt.value?.trim()) return;

            const response = await axios.post(ROLE_API.delete, {
                roleno: detail.roleno,
                delRsn: prompt.value.trim(),
            });
            const { result, resultMessage } = response.data.resultMap;
            if (result) {
                toastModalOpen(resultMessage);
                onDeleted();
            } else {
                showNotiModal(resultMessage);
            }
        } catch (error) {
            handleRoleApiError(error, showNotiModal, '롤 삭제 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className='role_form'>
            <div className='com-search'>
                <table>
                    <colgroup>
                        <col width='10%' />
                        <col width='23%' />
                        <col width='11%' />
                        <col width='23%' />
                        <col width='10%' />
                        <col width='23%' />
                    </colgroup>
                    <tbody>
                        <tr>
                            <th>롤코드</th>
                            <td><span className='role_readonly bold'>{detail?.roleno}</span></td>
                            <th>권한코드</th>
                            <td><span className='role_readonly'>{notSelected ? '' : `${detail.authname ?? ''} (${detail.authjobs})`}</span></td>
                            <th>사용자수 / 메뉴권한</th>
                            <td><span className='role_readonly'>{notSelected ? '' : `${detail.usercnt ?? 0}명 / ${detail.authcnt ?? 0}건${detail.authcntUnused > 0 ? ` (미사용 ${detail.authcntUnused})` : ''}`}</span></td>
                        </tr>
                        <tr>
                            <th><label htmlFor='role-detail-rolename'>롤명</label></th>
                            <td>
                                {notSelected ? '' :
                                    <input id='role-detail-rolename' type='text' className='input_style' name='rolename' maxLength={50}
                                        value={form.rolename} onChange={handleInputChange} />
                                }
                            </td>
                            <th><label htmlFor='role-detail-useyn'>사용여부</label></th>
                            <td>
                                {notSelected ? '' :
                                    <select id='role-detail-useyn' className='w-full' name='useyn' value={form.useyn} onChange={handleInputChange}>
                                        <option value='Y'>사용</option>
                                        <option value='N'>미사용</option>
                                    </select>
                                }
                            </td>
                            <th><label htmlFor='role-detail-defaultyn'>기본롤</label></th>
                            <td>
                                {notSelected ? '' :
                                    <label htmlFor='role-detail-defaultyn'>
                                        <input type='checkbox' id='role-detail-defaultyn' name='defaultyn' checked={form.defaultyn === 'Y'} onChange={handleInputChange} />
                                        권한코드 기본롤
                                    </label>
                                }
                            </td>
                        </tr>
                        <tr>
                            <th><label htmlFor='role-detail-bigo'>비고</label></th>
                            <td colSpan='5'>
                                {notSelected ? '' :
                                    <input id='role-detail-bigo' type='text' className='input_style' name='bigo' maxLength={2000}
                                        value={form.bigo} onChange={handleInputChange} />
                                }
                            </td>
                        </tr>
                        <tr>
                            <th>등록</th>
                            <td><span className='role_readonly'>{notSelected ? '' : `${detail.resId ?? ''} ${formatDt(detail.resDt)}`}</span></td>
                            <th>최종수정</th>
                            <td colSpan='3'><span className='role_readonly'>{notSelected ? '' : `${detail.updId ?? ''} ${formatDt(detail.updDt)}`}</span></td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div className='flex gap-2 py-2 justify-end'>
                <span className='role_form_note self-center text-right'>
                    ※ 권한코드·롤코드는 사용자·권한이 참조하는 키라 변경할 수 없습니다.<br />
                    ※ 삭제는 미사용 상태이면서 기본롤이 아니고 사용 중인 사용자가 없는 롤만 가능하며, 메뉴 접근권한이 함께 삭제되고 삭제 로그에 보관됩니다.
                </span>
                <button onClick={withPermission('UPYN', update)} className={`search_btn ${primaryBackColor}`} title='롤 정보 수정' disabled={notSelected || saving}>수정</button>
                <button onClick={withPermission('DLYN', remove)} className='search_btn bg_tr_primary' title='롤 삭제' disabled={notSelected || saving}>삭제</button>
            </div>
        </div>
    );
};

export default RoleDetail;
